/**
 * SeeMe Sidenav System
 * Two-panel flyout navigation: icon rail + submenu panel.
 *
 * Usage — add data attributes to wire up behavior:
 *
 *   <aside class="sidenav" data-sidenav>
 *     <nav class="sidenav__body">
 *       <ul class="nav">
 *         <li class="nav__item">
 *           <button class="nav__link" data-sidenav-trigger="deals">
 *             <span class="nav__icon">...</span>
 *             <span class="nav__label">Deals</span>
 *             <span class="nav__chevron">...</span>
 *           </button>
 *         </li>
 *       </ul>
 *     </nav>
 *     <div class="sidenav__panel" data-sidenav-panel>
 *       <div class="sidenav__panel-header">
 *         <h2 data-sidenav-title></h2>
 *         <button class="sidenav__panel-close" data-sidenav-close></button>
 *       </div>
 *       <div class="sidenav__panel-body" data-sidenav-content>
 *         <!-- Inline sections (show/hide) -->
 *         <div data-sidenav-section="deals" hidden>...</div>
 *         <div data-sidenav-section="rentals" hidden>...</div>
 *         <!-- OR: HTMX-loaded content (panel body has hx-get) -->
 *       </div>
 *     </div>
 *   </aside>
 *
 * Modes:
 *   1. Inline sections:  data-sidenav-section on children of panel body.
 *      JS shows the matching section, hides others.
 *   2. HTMX loading:     data-sidenav-url="..." on the trigger button.
 *      JS sets hx-get on the panel body and triggers htmx.
 *   3. Fetch loading:    Same as HTMX but falls back to fetch if htmx is not available.
 *
 * CSS classes managed:
 *   .sidenav--open       on [data-sidenav]        (panel visible, rail collapsed)
 *   .nav__link--active   on [data-sidenav-trigger] (active trigger)
 *   .sidenav-overlay--visible on .sidenav-overlay  (mobile backdrop)
 *
 * Events:
 *   "sidenav:open"   on [data-sidenav]  detail: { section }
 *   "sidenav:close"  on [data-sidenav]
 */

(function () {
    var activeSection = null;
    var contentCache = {};

    function getSidenav(el) {
        return el.closest('[data-sidenav]');
    }

    function open(sidenav, trigger, opts) {
        opts = opts || {};
        var section = trigger.getAttribute('data-sidenav-trigger');
        if (!section) return;

        // Same section — toggle closed
        if (activeSection === section && sidenav.classList.contains('sidenav--open')) {
            close(sidenav);
            return;
        }

        activeSection = section;
        sidenav.classList.add('sidenav--open');

        // Update active trigger
        sidenav.querySelectorAll('[data-sidenav-trigger]').forEach(function (t) {
            t.classList.toggle('nav__link--active', t.getAttribute('data-sidenav-trigger') === section);
        });

        // Update panel title
        var title = sidenav.querySelector('[data-sidenav-title]');
        if (title) {
            title.textContent = trigger.querySelector('.nav__label')
                ? trigger.querySelector('.nav__label').textContent.trim()
                : section;
        }

        // Load content
        var content = sidenav.querySelector('[data-sidenav-content]');
        if (!content) return;

        var url = trigger.getAttribute('data-sidenav-url');

        // Hide all inline sections first
        var sections = content.querySelectorAll('[data-sidenav-section]');
        sections.forEach(function (s) { s.hidden = true; });

        if (url) {
            // URL loading (HTMX or fetch) — takes priority
            loadContent(sidenav, content, url, section);
        } else {
            // Inline sections — show the matching one
            var match = content.querySelector('[data-sidenav-section="' + section + '"]');
            if (match) match.hidden = false;
        }

        // Show mobile overlay (skip for programmatic/auto-open on page load)
        if (!opts.silent) {
            var overlay = document.querySelector('.sidenav-overlay');
            if (overlay && window.innerWidth < 768) {
                overlay.classList.add('sidenav-overlay--visible');
            }
        }

        sidenav.dispatchEvent(new CustomEvent('sidenav:open', {
            detail: { section: section },
            bubbles: true
        }));
    }

    function close(sidenav) {
        if (!sidenav) return;
        activeSection = null;
        sidenav.classList.remove('sidenav--open');

        sidenav.querySelectorAll('[data-sidenav-trigger]').forEach(function (t) {
            t.classList.remove('nav__link--active');
        });

        // On mobile, keep overlay visible while sidenav is still open
        var overlay = document.querySelector('.sidenav-overlay');
        if (overlay && !sidenav.classList.contains('sidenav--mobile-open')) {
            overlay.classList.remove('sidenav-overlay--visible');
        }

        sidenav.dispatchEvent(new CustomEvent('sidenav:close', { bubbles: true }));
    }

    // ── Mobile slide-in ───────────────────────────────────────────────────
    // On mobile, sidenav is hidden off-screen via translateX(-100%).
    // Toggle sidenav--mobile-open to slide it in, with overlay backdrop.

    function mobileOpen(sidenav) {
        if (!sidenav) return;
        sidenav.classList.add('sidenav--mobile-open');

        var overlay = document.querySelector('.sidenav-overlay');
        if (overlay) overlay.classList.add('sidenav-overlay--visible');
    }

    function mobileClose(sidenav) {
        if (!sidenav) return;
        sidenav.classList.remove('sidenav--mobile-open');
        close(sidenav); // also close any open panel

        var overlay = document.querySelector('.sidenav-overlay');
        if (overlay) overlay.classList.remove('sidenav-overlay--visible');
    }

    function loadContent(sidenav, container, url, section) {
        // Check cache
        if (contentCache[section]) {
            container.innerHTML = contentCache[section];
            processContent(sidenav, container);
            return;
        }

        // HTMX mode
        if (typeof htmx !== 'undefined') {
            container.setAttribute('hx-get', url);
            container.setAttribute('hx-trigger', 'load');
            container.setAttribute('hx-swap', 'innerHTML');
            container.setAttribute('hx-target', 'this');
            container.removeAttribute('hx-select');
            container.removeAttribute('hx-push-url');
            htmx.process(container);
            htmx.trigger(container, 'load');

            // Cache after HTMX settles
            container.addEventListener('htmx:afterSettle', function handler() {
                contentCache[section] = container.innerHTML;
                processContent(sidenav, container);
                container.removeEventListener('htmx:afterSettle', handler);
            });
            return;
        }

        // Fetch fallback
        fetch(url)
            .then(function (r) { return r.text(); })
            .then(function (html) {
                container.innerHTML = html;
                contentCache[section] = html;
                processContent(sidenav, container);
            })
            .catch(function () {
                container.innerHTML = '<p style="padding:1rem;opacity:0.5">Failed to load</p>';
            });
    }

    function processContent(sidenav, container) {
        // Set active subnav link based on current URL
        var path = window.location.pathname;
        container.querySelectorAll('.subnav__link').forEach(function (link) {
            var href = link.getAttribute('href');
            if (href && path === href) {
                link.classList.add('subnav__link--active');
            }
        });

        // Process HTMX attributes on new content
        if (typeof htmx !== 'undefined') {
            htmx.process(container);
        }
    }

    // ── Event Listeners ──────────────────────────────────────────────────

    // Trigger click
    // ── Collapsible groups (.nav__group) ─────────────────────────────────
    var GROUPS_KEY = 'seeme.sidenav.groups';

    function readGroupState() {
        try { return JSON.parse(localStorage.getItem(GROUPS_KEY) || '{}'); } catch (e) { return {}; }
    }

    // A group's own toggle, never one from a group nested inside it
    function groupToggle(group) {
        return group.querySelector(':scope > .nav__heading--toggle, :scope > .nav__link-row > .nav__expand');
    }

    function setGroupCollapsed(group, collapsed) {
        group.classList.toggle('nav__group--collapsed', collapsed);
        var heading = groupToggle(group);
        if (heading) heading.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        var key = group.getAttribute('data-nav-group');
        if (key) {
            var state = readGroupState();
            state[key] = collapsed;
            try { localStorage.setItem(GROUPS_KEY, JSON.stringify(state)); } catch (e) {}
        }
    }

    // Apply the remembered state to every keyed group. Runs on load; a consumer
    // that re-renders the sidebar (e.g. an htmx out-of-band swap) calls
    // window.sidenav.restoreGroups() afterwards.
    function restoreGroups() {
        var state = readGroupState();
        document.querySelectorAll('.nav__group[data-nav-group]').forEach(function (group) {
            var key = group.getAttribute('data-nav-group');
            if (group.querySelector('.nav__link--active')) {
                // The current page's group is always open
                group.classList.remove('nav__group--collapsed');
            } else if (key in state) {
                group.classList.toggle('nav__group--collapsed', !!state[key]);
            }
            var heading = groupToggle(group);
            if (heading) heading.setAttribute('aria-expanded', group.classList.contains('nav__group--collapsed') ? 'false' : 'true');
        });
    }

    document.addEventListener('DOMContentLoaded', restoreGroups);

    document.addEventListener('click', function (e) {
        // Collapsible group heading
        var groupHeading = e.target.closest('.nav__heading--toggle, .nav__expand');
        if (groupHeading) {
            e.preventDefault();
            var group = groupHeading.closest('.nav__group');
            if (group) setGroupCollapsed(group, !group.classList.contains('nav__group--collapsed'));
            return;
        }

        // Mobile toggle button
        var toggle = e.target.closest('[data-sidenav-toggle]');
        if (toggle) {
            e.preventDefault();
            var sn = document.querySelector('[data-sidenav]');
            if (sn && sn.classList.contains('sidenav--mobile-open')) {
                mobileClose(sn);
            } else if (sn) {
                mobileOpen(sn);
            }
            return;
        }

        var trigger = e.target.closest('[data-sidenav-trigger]');
        if (trigger) {
            e.preventDefault();
            var sidenav = getSidenav(trigger);
            if (sidenav) open(sidenav, trigger);
            return;
        }

        // Close button
        var closeBtn = e.target.closest('[data-sidenav-close]');
        if (closeBtn) {
            e.preventDefault();
            close(getSidenav(closeBtn));
            return;
        }

        // Overlay click — close mobile and/or panel
        if (e.target.classList.contains('sidenav-overlay--visible')) {
            var sn = document.querySelector('[data-sidenav]');
            if (sn && sn.classList.contains('sidenav--mobile-open')) {
                mobileClose(sn);
            } else if (sn) {
                close(sn);
            }
            return;
        }

        // Panel link click on mobile — auto-close so the navigated content is visible.
        // Applies to HTMX-loaded panel content and inline [data-sidenav-section] content.
        var panelLink = e.target.closest('.sidenav__panel-body a, [data-sidenav-section] a');
        if (panelLink) {
            var snMobile = getSidenav(panelLink);
            if (snMobile && snMobile.classList.contains('sidenav--mobile-open')) {
                mobileClose(snMobile);
            }
        }
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
            var sn = document.querySelector('[data-sidenav]');
            if (sn && sn.classList.contains('sidenav--mobile-open')) {
                mobileClose(sn);
            } else if (sn && sn.classList.contains('sidenav--open')) {
                close(sn);
            }
        }
    });

    // Set active nav item on page load based on URL
    document.addEventListener('DOMContentLoaded', function () {
        var sidenav = document.querySelector('[data-sidenav]');
        if (!sidenav) return;

        var path = window.location.pathname;
        // Find subnav links that match and activate their parent trigger
        sidenav.querySelectorAll('[data-sidenav-section]').forEach(function (section) {
            section.querySelectorAll('a').forEach(function (link) {
                if (link.getAttribute('href') === path) {
                    var name = section.getAttribute('data-sidenav-section');
                    var trigger = sidenav.querySelector('[data-sidenav-trigger="' + name + '"]');
                    if (trigger) trigger.classList.add('nav__link--active');
                }
            });
        });
    });

    // ── Rail tooltip ─────────────────────────────────────────────────────
    // When sidenav is in rail mode (--open), labels are hidden.
    // Show a floating tooltip on hover, appended to <body> to escape overflow.
    var tip = null;

    function showTip(trigger) {
        var sn = getSidenav(trigger);
        if (!sn || !sn.classList.contains('sidenav--open')) return;
        var text = trigger.getAttribute('title');
        if (!text) return;

        // Suppress native title tooltip
        trigger.setAttribute('data-title', text);
        trigger.removeAttribute('title');

        tip = document.createElement('div');
        tip.className = 'sidenav-tooltip';
        tip.textContent = text;
        document.body.appendChild(tip);

        var rect = trigger.getBoundingClientRect();
        tip.style.top = (rect.top + rect.height / 2 - tip.offsetHeight / 2) + 'px';
        tip.style.left = (rect.right + 8) + 'px';
    }

    function hideTip(trigger) {
        if (tip) {
            tip.remove();
            tip = null;
        }
        // Restore title
        var saved = trigger.getAttribute('data-title');
        if (saved) {
            trigger.setAttribute('title', saved);
            trigger.removeAttribute('data-title');
        }
    }

    document.addEventListener('mouseenter', function (e) {
        if (!e.target.closest) return;
        var trigger = e.target.closest('[data-sidenav-trigger]');
        if (trigger) showTip(trigger);
    }, true);

    document.addEventListener('mouseleave', function (e) {
        if (!e.target.closest) return;
        var trigger = e.target.closest('[data-sidenav-trigger]');
        if (trigger) hideTip(trigger);
    }, true);

    // Expose for programmatic use
    window.sidenav = {
        open: function (section, opts) {
            var sn = document.querySelector('[data-sidenav]');
            var trigger = sn && sn.querySelector('[data-sidenav-trigger="' + section + '"]');
            if (sn && trigger) open(sn, trigger, opts);
        },
        close: function () {
            var sn = document.querySelector('[data-sidenav]');
            if (sn) close(sn);
        },
        mobileOpen: function () {
            var sn = document.querySelector('[data-sidenav]');
            if (sn) mobileOpen(sn);
        },
        restoreGroups: restoreGroups,
        mobileClose: function () {
            var sn = document.querySelector('[data-sidenav]');
            if (sn) mobileClose(sn);
        }
    };
})();
